import React, { PureComponent } from 'react';
import { connect } from 'react-redux';
import PropTypes from 'prop-types';
import bindMethods from 'auto-bind';
import styled from 'styled-components';
import { darken } from 'polished';
import { withTranslation } from 'react-i18next';

import WebsocketConnect from '../websocket';
import Loading from '../../layouts/WebsocketLoadingSpinner';
import { setWebsocketMessage, toggleWebsocketError, setWebsocketPercentDone } from '../../state/settings';
import { getQlikAppId } from '../../state/selectors/getQlikAppId';
import { XOS_RED } from '../../constants/colors';
import {
  WEBSOCKET_CONNECTION_INIT,
  WEBSOCKET_CONNECTION_CLOSE,
  WEBSOCKET_LOADING_MESSAGE,
  WEBSOCKET_DONE_MESSAGE,
  WEBSOCKET_GENERAL_ERROR,
} from '../../constants/loading';

const LoadingContainer = styled.div`
  display: flex;
  padding: 20px;
  flex-direction: column;
  align-items: center;
`;

const LoadingText = styled.div`
  display: flex;
  justify-content: center;
  width: 100%;
  font-weight: 500;
  font-size: 16px;
`;

const PercentDoneText = styled.div`
  display: flex;
  justify-content: center;
  width: 100%;
  font-weight: 500;
  font-size: 14px;
  margin-top: 7px;
`;

const ModalFooter = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
`;

const ModalButton = styled.button`
  display: block;
  width: 180px;
  border: none;
  border-radius: 4px;
  font-size: 16px;
  height: 40px;
  background-color: ${XOS_RED};
  color: #fff;
  cursor: pointer;

  :hover {
    background: ${darken(0.05, XOS_RED)};
  }
`;

class WebSocketIndicator extends PureComponent {
  constructor(props) {
    super(props);
    bindMethods(this);
  }

  handleWebsocketData(data) {
    const { setWebsocketMessage, toggleWebsocketError, setWebsocketPercentDone } = this.props;

    try {
      const results = JSON.parse(data);

      setWebsocketPercentDone(data);

      if (results.error) {
        toggleWebsocketError(true);
      }
    } catch (error) {
      if (data === WEBSOCKET_CONNECTION_INIT) setWebsocketMessage(WEBSOCKET_LOADING_MESSAGE);
      if (data === WEBSOCKET_CONNECTION_CLOSE) {
        setWebsocketMessage(WEBSOCKET_DONE_MESSAGE);
        setWebsocketPercentDone('');
      }
    }
  }

  reloadApplication() {
    window.location.reload();
  }

  render() {
    const {
      appId, websocketMessage, websocketError, websocketPercentDone, t,
    } = this.props;
    const isLoading = websocketMessage !== WEBSOCKET_DONE_MESSAGE || websocketError;

    let percentLabel = '';
    const percentDone = Number.parseInt(websocketPercentDone);
    if (!Number.isNaN(percentDone)) {
      percentLabel = `${percentDone}%`;
    }
    return (
      <React.Fragment>
        <WebsocketConnect
          appId={appId}
          url={process.env.REACT_APP_DASHBOARD_WEBSOCKET_URL}
          onMessage={this.handleWebsocketData}
        />
        <LoadingContainer>
          {!websocketError && <Loading isLoading={isLoading} />}
          <LoadingText>{websocketError ? WEBSOCKET_GENERAL_ERROR : websocketMessage}</LoadingText>
          <PercentDoneText>{percentLabel}</PercentDoneText>
        </LoadingContainer>
        {!isLoading || websocketError ? (
          <ModalFooter>
            <ModalButton
              id="GeneralSettings.ApplicationModal.ReloadApplication"
              onClick={this.reloadApplication}
            >
              {t('NavigationBar.User.General.ReloadApplication')}
            </ModalButton>
          </ModalFooter>
        ) : null}
      </React.Fragment>
    );
  }
}

WebSocketIndicator.propTypes = {
  t: PropTypes.func.isRequired,
  appId: PropTypes.string.isRequired,
  setWebsocketMessage: PropTypes.func.isRequired,
  setWebsocketPercentDone: PropTypes.func.isRequired,
  toggleWebsocketError: PropTypes.func.isRequired,
  websocketMessage: PropTypes.oneOfType([PropTypes.string, PropTypes.object]),
  websocketError: PropTypes.bool.isRequired,
  websocketPercentDone: PropTypes.oneOfType([PropTypes.string, PropTypes.object]),
};

const mapStateToProps = state => ({
  appId: getQlikAppId(state),
  websocketConnect: state.settings.websocket.connect,
  websocketMessage: state.settings.websocket.message,
  websocketError: state.settings.websocket.error,
  websocketPercentDone: state.settings.websocket.percentdone,
});

const mapDispatchToProps = {
  setWebsocketMessage,
  setWebsocketPercentDone,
  toggleWebsocketError,
};

const TranslatedComponent = withTranslation()(WebSocketIndicator);

export default connect(
  mapStateToProps,
  mapDispatchToProps,
)(TranslatedComponent);
